/**
 * Realtime Event Schemas for Upstash Realtime SDK
 *
 * Defines Zod schemas for realtime events used with @upstash/realtime SDK.
 * These schemas follow the SDK's category.event naming convention.
 *
 * @module schemas/realtime-events
 * @see SPEC-007-6 Requirements 2.1-2.9
 */
import { z } from 'zod';
import { COLOR_ID_MIN, COLOR_ID_MAX, MAX_DISPLAY_NAME_LENGTH } from '../types/presence';
/**
 * Position schema - cursor position in editor
 * Reuses the same structure as presence.zod.ts but defined locally
 * to avoid circular dependencies with InferSchema
 */
const PositionSchema = z.object({
    line: z.number().int().min(0),
    column: z.number().int().min(0),
});
/**
 * Selection schema - text selection range (nullable when no selection)
 */
const SelectionSchema = z
    .object({
    anchor: PositionSchema,
    head: PositionSchema,
})
    .nullable();
/**
 * Realtime Event Schema for Upstash Realtime SDK
 *
 * Categories:
 * - presence: User presence events (cursor, selection, join/leave)
 * - sync: Document sync events (for SPEC-008 CRDT)
 *
 * @example
 * ```typescript
 * import { createRealtime } from '@upstash/realtime/client';
 * import { RealtimeEventSchema } from '@linkode/shared';
 *
 * const { useRealtime } = createRealtime<InferSchema<typeof RealtimeEventSchema>>();
 * ```
 */
export const RealtimeEventSchema = {
    presence: {
        /**
         * Presence update event
         * Sent when user moves cursor or changes selection
         * @see SPEC-007-6 Requirement 2.4
         */
        update: z.object({
            userId: z.string().min(1),
            cursor: PositionSchema.nullable(),
            selection: SelectionSchema,
            displayName: z.string().max(MAX_DISPLAY_NAME_LENGTH),
            colorId: z.number().int().min(COLOR_ID_MIN).max(COLOR_ID_MAX),
        }),
        /**
         * User joined room event
         * @see SPEC-007-6 Requirement 2.5
         */
        join: z.object({
            userId: z.string().min(1),
            displayName: z.string().max(MAX_DISPLAY_NAME_LENGTH),
            colorId: z.number().int().min(COLOR_ID_MIN).max(COLOR_ID_MAX),
        }),
        /**
         * User left room event
         * @see SPEC-007-6 Requirement 2.6
         */
        leave: z.object({
            userId: z.string().min(1),
        }),
    },
    sync: {
        /**
         * Document delta sync event
         * Used by SPEC-008 for CRDT updates
         * @see SPEC-007-6 Requirement 2.7
         */
        delta: z.object({
            docId: z.string().min(1),
            changes: z.unknown(), // Opaque blob, validated by SPEC-008
            version: z.number().int().optional(),
        }),
    },
};
//# sourceMappingURL=realtime-events.zod.js.map